import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
  Card,
  CardBody
} from "reactstrap";
import {
  FileText,
  Printer,
  User,
  Briefcase,
  BookOpen,
  CheckCircle,
  RefreshCw,
  Download,
  Plus,
  Trash2,
  Award,
  MapPin,
  Phone,
  Mail,
  Linkedin,
  Sliders,
  Eye,
  Check
} from "react-feather";
import Notiflix from "notiflix";

// Available Accent Themes
const COLOR_THEMES = [
  { id: "navy", name: "Corporate Navy", primary: "#1e3a8a", secondary: "#3b82f6", light: "#eff6ff", text: "#1e293b" },
  { id: "emerald", name: "Executive Emerald", primary: "#065f46", secondary: "#10b981", light: "#ecfdf5", text: "#1e293b" },
  { id: "slate", name: "Modern Charcoal", primary: "#1e293b", secondary: "#64748b", light: "#f8fafc", text: "#0f172a" },
  { id: "burgundy", name: "Distinguished Wine", primary: "#881337", secondary: "#e11d48", light: "#fff1f2", text: "#1e293b" },
  { id: "indigo", name: "Tech Indigo", primary: "#3730a3", secondary: "#6366f1", light: "#eef2ff", text: "#1e293b" },
  { id: "teal", name: "Nordic Teal", primary: "#0f766e", secondary: "#14b8a6", light: "#f0fdfa", text: "#1e293b" }
];

// Available Typography Options
const FONT_PRESETS = [
  { id: "sans", name: "Modern Clean (Sans)", fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
  { id: "serif", name: "Executive Classic (Serif)", fontFamily: "'Merriweather', Georgia, 'Times New Roman', serif" },
  { id: "system", name: "Technical Sharp (System)", fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif" }
];

// Available Resume Templates
const TEMPLATES = [
  {
    id: "modern",
    name: "Modern ATS Pro",
    description: "Balanced dual-tone layout with structured sections and high ATS readability",
    badge: "Recommended"
  },
  {
    id: "executive",
    name: "Executive Slate",
    description: "Bold header banner with premium typography, tailored for mid to senior professionals",
    badge: "Popular"
  },
  {
    id: "minimal",
    name: "Clean ATS Minimal",
    description: "Streamlined single-column format optimized for 100% parsing on Workday & Taleo",
    badge: "ATS 100%"
  },
  {
    id: "split",
    name: "Sidebar Professional",
    description: "Contemporary two-column format with dedicated skills & credentials sidebar",
    badge: "Visual"
  }
];

const ResumeBuilderModal = ({
  isOpen,
  toggle,
  userData = {},
  formikValues = {},
  onSaveResume
}) => {
  const [activeTab, setActiveTab] = useState("1"); // 1: Content Editor, 2: Design & Template, 3: Live Preview
  const [selectedTemplate, setSelectedTemplate] = useState("modern");
  const [selectedTheme, setSelectedTheme] = useState(COLOR_THEMES[0]);
  const [selectedFont, setSelectedFont] = useState(FONT_PRESETS[0]);
  const [newSkillInput, setNewSkillInput] = useState("");

  // Candidate-specific storage key so different candidate profiles do not overwrite each other
  const candidateIdentifier =
    userData?.fld_id ||
    userData?.id ||
    formikValues?.AuditorGmail ||
    userData?.fld_email ||
    formikValues?.AuditorContact ||
    userData?.fld_mobile ||
    "default_candidate";
  const DRAFT_STORAGE_KEY = `resume_builder_draft_${candidateIdentifier}`;

  const isLoadedRef = useRef(false);

  // Comprehensive Resume Data Structure
  const [resumeData, setResumeData] = useState({
    fullName: "",
    jobTitle: "",
    email: "",
    phone: "",
    location: "",
    address: "",
    linkedin: "",
    website: "",
    photoUrl: "",
    summary: "",
    experiences: [],
    educations: [],
    skills: [],
    certifications: [],
    languages: []
  });

  // Autofill function that syncs profile and formik data
  const handleAutoFillFromProfile = (showNotification = true) => {
    const name = formikValues?.AuditorName || userData?.fld_name || "";
    const email = formikValues?.AuditorGmail || userData?.fld_email || "";
    const phone = formikValues?.AuditorContact || userData?.fld_mobile || "";
    const role = formikValues?.usertype?.label || formikValues?.usertype?.value || userData?.fld_usertype || "Professional";

    const city = formikValues?.AuditorCity?.label || formikValues?.AuditorCity?.value || userData?.fld_presentcity || "";
    const state = formikValues?.AuditorState?.label || formikValues?.AuditorState?.value || userData?.fld_presentstate || "";
    const country = formikValues?.AuditorCountry?.label || formikValues?.AuditorCountry?.value || userData?.fld_presentcountry || "India";
    const loc = [city, state, country].filter(Boolean).join(", ");
    const addr = formikValues?.AuditorAddress || userData?.fld_presentaddress || "";

    const qual = formikValues?.AuditorQual?.label || formikValues?.AuditorQual?.value || userData?.fld_qualification || "";
    const expYears = formikValues?.TotalExperience || userData?.fld_experience || "";
    const prevCompany = formikValues?.PreEmployeeName || userData?.fld_prevcompname || "";
    const summaryText =
      formikValues?.WorkExperience ||
      userData?.fld_worksummary ||
      (name
        ? `Dedicated and results-oriented ${role} with ${expYears ? `${expYears} years of ` : ""}proven expertise in quality auditing, process compliance, documentation, and operational excellence. Adept at cross-functional collaboration, problem solving, and driving organizational goals.`
        : "");

    // Extract skills/preferred cities
    let skillsList = [];
    if (Array.isArray(formikValues?.WorkCity)) {
      skillsList = formikValues.WorkCity.map((c) => c.label || c.value || c);
    } else if (typeof userData?.work_preference === "string" && userData.work_preference.trim()) {
      skillsList = userData.work_preference.split(",").map((s) => s.trim());
    }

    if (skillsList.length === 0) {
      skillsList = ["Quality Audit", "Compliance Review", "Process Analysis", "Data Verification", "Documentation", "Risk Assessment"];
    }

    // Photo URL
    const photo =
      userData?.fld_photo &&
        !userData.fld_photo.startsWith("blob:") &&
        process.env.REACT_APP_FILE_PATH_URL
        ? process.env.REACT_APP_FILE_PATH_URL + userData.fld_photo
        : "";

    // Experience Items
    const initialExperiences = [];
    if (prevCompany || expYears) {
      initialExperiences.push({
        id: Date.now(),
        title: role || "Senior Auditor",
        company: prevCompany || "Independent Professional",
        location: loc || "India",
        startDate: "2022",
        endDate: "Present",
        isCurrent: true,
        description: `• Conducted regular quality audits and compliance evaluations ensuring adherence to corporate standards.\n• Prepared comprehensive audit reports, risk registers, and actionable improvement recommendations.\n• Collaborated with departmental stakeholders to streamline operational workflows and eliminate bottlenecks.`
      });
    }

    // Education Items
    const initialEducations = [];
    if (qual) {
      initialEducations.push({
        id: Date.now() + 1,
        degree: qual,
        institution: "Recognized University / Institution",
        location: state || "India",
        year: "2020",
        grade: "First Class / Honors"
      });
    }

    setResumeData({
      fullName: name,
      jobTitle: role,
      email: email,
      phone: phone,
      location: loc,
      address: addr,
      linkedin: "",
      website: "",
      photoUrl: photo,
      summary: summaryText,
      experiences: initialExperiences.length > 0 ? initialExperiences : [
        {
          id: 1,
          title: "Quality & Compliance Specialist",
          company: "Enterprise Services Ltd.",
          location: "Mumbai, India",
          startDate: "2021",
          endDate: "Present",
          isCurrent: true,
          description: "• Directed operational compliance checks and quality verification across key project deliverables.\n• Analyzed documentation for accuracy and mitigated compliance risks across teams."
        }
      ],
      educations: initialEducations.length > 0 ? initialEducations : [
        {
          id: 1,
          degree: qual || "Bachelor's Degree",
          institution: "State University",
          location: "India",
          year: "2020",
          grade: "Distinction"
        }
      ],
      skills: skillsList,
      certifications: [
        { id: 1, name: "Certified Internal Auditor / ISO Compliance", issuer: "Quality Council", year: "2023" }
      ],
      languages: ["English (Professional)", "Hindi (Native)"]
    });

    if (showNotification) {
      Notiflix.Notify.success("Profile details imported successfully!");
    }
  };

  // Load draft from localStorage on open, or fallback to auto-fill if no draft exists
  useEffect(() => {
    if (isOpen) {
      try {
        const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
        if (savedDraft) {
          const parsed = JSON.parse(savedDraft);
          if (parsed && typeof parsed === "object") {
            if (parsed.resumeData) {
              setResumeData(parsed.resumeData);
            }
            if (parsed.selectedTemplate) {
              setSelectedTemplate(parsed.selectedTemplate);
            }
            if (parsed.selectedThemeId) {
              const matchedTheme = COLOR_THEMES.find((t) => t.id === parsed.selectedThemeId);
              if (matchedTheme) setSelectedTheme(matchedTheme);
            } else if (parsed.selectedTheme) {
              setSelectedTheme(parsed.selectedTheme);
            }
            if (parsed.selectedFontId) {
              const matchedFont = FONT_PRESETS.find((f) => f.id === parsed.selectedFontId);
              if (matchedFont) setSelectedFont(matchedFont);
            } else if (parsed.selectedFont) {
              setSelectedFont(parsed.selectedFont);
            }
            isLoadedRef.current = true;
            return;
          }
        }
      } catch (err) {
        console.warn("Could not parse saved resume draft from localStorage", err);
      }

      // No previous draft found -> perform initial profile autofill
      handleAutoFillFromProfile(false);
      isLoadedRef.current = true;
    } else {
      isLoadedRef.current = false;
    }
  }, [isOpen, DRAFT_STORAGE_KEY]);

  // Auto-save changes to localStorage as the user edits or customizes
  useEffect(() => {
    if (!isOpen || !isLoadedRef.current) return;
    try {
      const payload = {
        resumeData,
        selectedTemplate,
        selectedThemeId: selectedTheme?.id,
        selectedTheme,
        selectedFontId: selectedFont?.id,
        selectedFont,
        updatedAt: new Date().toISOString()
      };
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(payload));
    } catch (err) {
      console.warn("Could not auto-save resume draft to localStorage", err);
    }
  }, [resumeData, selectedTemplate, selectedTheme, selectedFont, isOpen, DRAFT_STORAGE_KEY]);

  // Generic Field updater
  const handleFieldChange = (field, value) => {
    setResumeData((prev) => ({ ...prev, [field]: value }));
  };

  // --- EXPERIENCE ITEM HANDLERS ---
  const handleAddExperience = () => {
    const newExp = {
      id: Date.now(),
      title: "",
      company: "",
      location: "",
      startDate: "",
      endDate: "",
      isCurrent: false,
      description: ""
    };
    setResumeData((prev) => ({
      ...prev,
      experiences: [newExp, ...prev.experiences]
    }));
  };

  const handleUpdateExperience = (id, field, value) => {
    setResumeData((prev) => ({
      ...prev,
      experiences: prev.experiences.map((exp) =>
        exp.id === id ? { ...exp, [field]: value } : exp
      )
    }));
  };

  const handleDeleteExperience = (id) => {
    setResumeData((prev) => ({
      ...prev,
      experiences: prev.experiences.filter((exp) => exp.id !== id)
    }));
  };

  // --- EDUCATION ITEM HANDLERS ---
  const handleAddEducation = () => {
    const newEdu = {
      id: Date.now(),
      degree: "",
      institution: "",
      location: "",
      year: "",
      grade: ""
    };
    setResumeData((prev) => ({
      ...prev,
      educations: [newEdu, ...prev.educations]
    }));
  };

  const handleUpdateEducation = (id, field, value) => {
    setResumeData((prev) => ({
      ...prev,
      educations: prev.educations.map((edu) =>
        edu.id === id ? { ...edu, [field]: value } : edu
      )
    }));
  };

  const handleDeleteEducation = (id) => {
    setResumeData((prev) => ({
      ...prev,
      educations: prev.educations.filter((edu) => edu.id !== id)
    }));
  };

  // --- SKILLS HANDLERS ---
  const handleAddSkill = () => {
    if (!newSkillInput.trim()) return;
    if (!resumeData.skills.includes(newSkillInput.trim())) {
      setResumeData((prev) => ({
        ...prev,
        skills: [...prev.skills, newSkillInput.trim()]
      }));
    }
    setNewSkillInput("");
  };

  const handleDeleteSkill = (skillToRemove) => {
    setResumeData((prev) => ({
      ...prev,
      skills: prev.skills.filter((s) => s !== skillToRemove)
    }));
  };

  // --- CERTIFICATION HANDLERS ---
  const handleAddCertification = () => {
    const newCert = { id: Date.now(), name: "", issuer: "", year: "" };
    setResumeData((prev) => ({
      ...prev,
      certifications: [...prev.certifications, newCert]
    }));
  };

  const handleUpdateCertification = (id, field, value) => {
    setResumeData((prev) => ({
      ...prev,
      certifications: prev.certifications.map((cert) =>
        cert.id === id ? { ...cert, [field]: value } : cert
      )
    }));
  };

  const handleDeleteCertification = (id) => {
    setResumeData((prev) => ({
      ...prev,
      certifications: prev.certifications.filter((cert) => cert.id !== id)
    }));
  };

  // Generate Complete HTML Document for Standalone / Printing / Export
  const generateCompleteResumeHtml = () => {
    const printContent = document.getElementById("printable-resume-target");
    const contentHtml = printContent ? printContent.innerHTML : "";

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Resume - ${resumeData.fullName || "Candidate"}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Merriweather:wght@300;400;700&display=swap" rel="stylesheet">
  <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap@4.6.2/dist/css/bootstrap.min.css">
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: ${selectedFont.fontFamily};
      color: ${selectedTheme.text};
      background: #f1f5f9;
      margin: 0;
      padding: 30px 15px;
      line-height: 1.6;
      -webkit-font-smoothing: antialiased;
    }
    .resume-container {
      max-width: 850px;
      margin: 0 auto;
      background: #ffffff;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
    }
    .resume-content-body {
      padding: 35px 40px;
    }
    @media print {
      body {
        padding: 0;
        background: #ffffff;
        color: #000000;
      }
      .resume-container {
        box-shadow: none;
        border-radius: 0;
        max-width: 100%;
        margin: 0;
      }
      .resume-content-body {
        padding: 0;
      }
      .no-print {
        display: none !important;
      }
      @page {
        size: A4;
        margin: 12mm 15mm;
      }
      .resume-section {
        page-break-inside: avoid;
      }
    }
  </style>
</head>
<body>
  <div class="resume-container">
    ${contentHtml}
  </div>
</body>
</html>`;
  };

  // High-Resolution Print Handler
  const handlePrint = () => {
    const printWindow = window.open("", "_blank", "height=900,width=850");
    if (!printWindow) {
      Notiflix.Notify.failure("Please allow popups to print / export resume.");
      return;
    }
    printWindow.document.open();
    printWindow.document.write(generateCompleteResumeHtml());
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 600);
  };

  // Standalone HTML Download Handler
  const handleDownloadHtml = () => {
    const htmlString = generateCompleteResumeHtml();
    const blob = new Blob([htmlString], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${(resumeData.fullName || "Resume").replace(/\s+/g, "_")}_Resume.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    Notiflix.Notify.success("Resume downloaded as HTML file!");
  };

  // Attach to Profile Action
  const handleAttachToProfile = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const htmlString = generateCompleteResumeHtml();
    const blob = new Blob([htmlString], { type: "text/html" });
    const generatedFile = new File(
      [blob],
      `${(resumeData.fullName || "Resume").replace(/\s+/g, "_")}_Resume.html`,
      { type: "text/html" }
    );

    if (onSaveResume) {
      onSaveResume(generatedFile);
      Notiflix.Notify.success("Resume attached to profile successfully!");
    }
    toggle();
  };

  // ==========================================
  // TEMPLATE RENDERERS
  // ==========================================

  // 1. MODERN ATS PRO TEMPLATE
  const renderModernTemplate = () => {
    return (
      <div className="resume-content-body" style={{ color: selectedTheme.text }}>
        {/* Top Accent Strip */}
        <div style={{ height: "6px", background: `linear-gradient(90deg, ${selectedTheme.primary}, ${selectedTheme.secondary})`, margin: "-35px -40px 30px -40px" }} />

        {/* Header */}
        <div className="d-flex justify-content-between align-items-start pb-4 mb-4 border-bottom" style={{ borderColor: "#e2e8f0" }}>
          <div>
            <h1 className="h2 font-weight-bold mb-1" style={{ color: selectedTheme.primary, letterSpacing: "-0.5px" }}>
              {resumeData.fullName || "YOUR FULL NAME"}
            </h1>
            <div className="h6 font-weight-bold text-uppercase mb-3" style={{ color: selectedTheme.secondary, letterSpacing: "1px" }}>
              {resumeData.jobTitle || "TARGET JOB TITLE / ROLE"}
            </div>
            <div className="d-flex flex-wrap text-muted small" style={{ gap: "15px", fontSize: "13px" }}>
              {resumeData.email && (
                <div className="d-flex align-items-center">
                  <Mail size={13} className="mr-1" style={{ color: selectedTheme.primary }} />
                  <span>{resumeData.email}</span>
                </div>
              )}
              {resumeData.phone && (
                <div className="d-flex align-items-center">
                  <Phone size={13} className="mr-1" style={{ color: selectedTheme.primary }} />
                  <span>{resumeData.phone}</span>
                </div>
              )}
              {resumeData.location && (
                <div className="d-flex align-items-center">
                  <MapPin size={13} className="mr-1" style={{ color: selectedTheme.primary }} />
                  <span>{resumeData.location}</span>
                </div>
              )}
              {resumeData.linkedin && (
                <div className="d-flex align-items-center">
                  <Linkedin size={13} className="mr-1" style={{ color: selectedTheme.primary }} />
                  <span>{resumeData.linkedin}</span>
                </div>
              )}
            </div>
          </div>

          {resumeData.photoUrl && (
            <div
              style={{
                width: "95px",
                height: "95px",
                borderRadius: "10px",
                overflow: "hidden",
                border: `3px solid ${selectedTheme.light}`,
                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                flexShrink: 0
              }}
            >
              <img
                src={resumeData.photoUrl}
                alt="Profile"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>
          )}
        </div>

        {/* Professional Summary */}
        {resumeData.summary && (
          <div className="resume-section mb-4">
            <h6
              className="text-uppercase font-weight-bold mb-2 pb-1"
              style={{
                color: selectedTheme.primary,
                borderBottom: `2px solid ${selectedTheme.light}`,
                letterSpacing: "0.5px"
              }}
            >
              Professional Summary
            </h6>
            <p className="small mb-0" style={{ lineHeight: "1.7", color: "#475569", whiteSpace: "pre-line" }}>
              {resumeData.summary}
            </p>
          </div>
        )}

        {/* Work Experience */}
        {resumeData.experiences && resumeData.experiences.length > 0 && (
          <div className="resume-section mb-4">
            <h6
              className="text-uppercase font-weight-bold mb-3 pb-1"
              style={{
                color: selectedTheme.primary,
                borderBottom: `2px solid ${selectedTheme.light}`,
                letterSpacing: "0.5px"
              }}
            >
              Work Experience
            </h6>
            {resumeData.experiences.map((exp, idx) => (
              <div key={idx} className="mb-3">
                <div className="d-flex justify-content-between align-items-baseline flex-wrap">
                  <div className="font-weight-bold text-dark" style={{ fontSize: "14px" }}>
                    {exp.title || "Job Position"}
                    {exp.company && <span style={{ color: selectedTheme.secondary, fontWeight: "600" }}> | {exp.company}</span>}
                  </div>
                  <div className="small font-weight-bold" style={{ color: "#64748b" }}>
                    {exp.startDate || "Date"} – {exp.isCurrent ? "Present" : exp.endDate || "Present"}
                  </div>
                </div>
                {exp.location && <div className="small text-muted mb-1">{exp.location}</div>}
                {exp.description && (
                  <div className="small text-secondary mt-1" style={{ lineHeight: "1.6", whiteSpace: "pre-line" }}>
                    {exp.description}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Education & Qualifications */}
        {resumeData.educations && resumeData.educations.length > 0 && (
          <div className="resume-section mb-4">
            <h6
              className="text-uppercase font-weight-bold mb-3 pb-1"
              style={{
                color: selectedTheme.primary,
                borderBottom: `2px solid ${selectedTheme.light}`,
                letterSpacing: "0.5px"
              }}
            >
              Education & Qualifications
            </h6>
            {resumeData.educations.map((edu, idx) => (
              <div key={idx} className="mb-2">
                <div className="d-flex justify-content-between align-items-baseline flex-wrap">
                  <div className="font-weight-bold text-dark" style={{ fontSize: "14px" }}>
                    {edu.degree || "Degree / Qualification"}
                  </div>
                  <div className="small font-weight-bold" style={{ color: "#64748b" }}>
                    {edu.year || "Year"}
                  </div>
                </div>
                <div className="small text-muted">
                  {edu.institution || "Institution Name"} {edu.location ? `• ${edu.location}` : ""}{" "}
                  {edu.grade ? `(${edu.grade})` : ""}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Skills & Core Competencies */}
        {resumeData.skills && resumeData.skills.length > 0 && (
          <div className="resume-section mb-4">
            <h6
              className="text-uppercase font-weight-bold mb-2 pb-1"
              style={{
                color: selectedTheme.primary,
                borderBottom: `2px solid ${selectedTheme.light}`,
                letterSpacing: "0.5px"
              }}
            >
              Key Skills & Competencies
            </h6>
            <div className="d-flex flex-wrap" style={{ gap: "6px" }}>
              {resumeData.skills.map((skill, idx) => (
                <span
                  key={idx}
                  style={{
                    backgroundColor: selectedTheme.light,
                    color: selectedTheme.primary,
                    border: `1px solid ${selectedTheme.secondary}30`,
                    padding: "4px 10px",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: "600"
                  }}
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Certifications & Awards */}
        {resumeData.certifications && resumeData.certifications.length > 0 && (
          <div className="resume-section mb-2">
            <h6
              className="text-uppercase font-weight-bold mb-2 pb-1"
              style={{
                color: selectedTheme.primary,
                borderBottom: `2px solid ${selectedTheme.light}`,
                letterSpacing: "0.5px"
              }}
            >
              Certifications & Accreditations
            </h6>
            <ul className="small text-secondary pl-3 mb-0" style={{ lineHeight: "1.7" }}>
              {resumeData.certifications.map((cert, idx) => (
                <li key={idx}>
                  <strong>{cert.name}</strong> {cert.issuer ? `– ${cert.issuer}` : ""} {cert.year ? `(${cert.year})` : ""}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // 2. EXECUTIVE SLATE TEMPLATE
  const renderExecutiveTemplate = () => {
    return (
      <div style={{ color: selectedTheme.text }}>
        {/* Dark Header Banner */}
        <div style={{ backgroundColor: selectedTheme.primary, color: "#ffffff", padding: "35px 40px" }}>
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <h1 className="h2 font-weight-bold mb-1 text-white" style={{ letterSpacing: "0.5px" }}>
                {resumeData.fullName || "YOUR FULL NAME"}
              </h1>
              <div className="font-weight-bold text-uppercase" style={{ color: "#93c5fd", letterSpacing: "1.5px", fontSize: "14px" }}>
                {resumeData.jobTitle || "EXECUTIVE PROFILE"}
              </div>
            </div>
            {resumeData.photoUrl && (
              <div
                style={{
                  width: "85px",
                  height: "85px",
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: "3px solid #ffffff",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.2)"
                }}
              >
                <img src={resumeData.photoUrl} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
            )}
          </div>

          <div className="d-flex flex-wrap mt-3 pt-3 border-top" style={{ borderColor: "rgba(255,255,255,0.2)", gap: "18px", fontSize: "13px", color: "#e2e8f0" }}>
            {resumeData.email && <div>✉ {resumeData.email}</div>}
            {resumeData.phone && <div>📞 {resumeData.phone}</div>}
            {resumeData.location && <div>📍 {resumeData.location}</div>}
            {resumeData.linkedin && <div>🔗 {resumeData.linkedin}</div>}
          </div>
        </div>

        <div className="resume-content-body">
          {/* Executive Summary */}
          {resumeData.summary && (
            <div className="resume-section mb-4">
              <div className="d-flex align-items-center mb-2">
                <div style={{ width: "4px", height: "18px", backgroundColor: selectedTheme.primary, marginRight: "8px" }} />
                <h6 className="text-uppercase font-weight-bold mb-0" style={{ color: selectedTheme.primary, letterSpacing: "1px" }}>
                  Executive Summary
                </h6>
              </div>
              <p className="small mb-0" style={{ lineHeight: "1.7", color: "#334155", whiteSpace: "pre-line" }}>
                {resumeData.summary}
              </p>
            </div>
          )}

          {/* Experience */}
          {resumeData.experiences && resumeData.experiences.length > 0 && (
            <div className="resume-section mb-4">
              <div className="d-flex align-items-center mb-3">
                <div style={{ width: "4px", height: "18px", backgroundColor: selectedTheme.primary, marginRight: "8px" }} />
                <h6 className="text-uppercase font-weight-bold mb-0" style={{ color: selectedTheme.primary, letterSpacing: "1px" }}>
                  Professional Career History
                </h6>
              </div>
              {resumeData.experiences.map((exp, idx) => (
                <div key={idx} className="mb-3 pl-3" style={{ borderLeft: `2px solid #e2e8f0` }}>
                  <div className="d-flex justify-content-between align-items-baseline flex-wrap">
                    <span className="font-weight-bold text-dark" style={{ fontSize: "14px" }}>
                      {exp.title}
                    </span>
                    <span className="badge badge-light border text-muted">{exp.startDate} – {exp.isCurrent ? "Present" : exp.endDate}</span>
                  </div>
                  <div className="small font-weight-bold" style={{ color: selectedTheme.secondary }}>
                    {exp.company} {exp.location ? `• ${exp.location}` : ""}
                  </div>
                  {exp.description && (
                    <div className="small text-secondary mt-2" style={{ lineHeight: "1.6", whiteSpace: "pre-line" }}>
                      {exp.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Education & Skills Split */}
          <Row>
            <Col sm="6">
              {resumeData.educations && resumeData.educations.length > 0 && (
                <div className="resume-section mb-4">
                  <div className="d-flex align-items-center mb-2">
                    <div style={{ width: "4px", height: "18px", backgroundColor: selectedTheme.primary, marginRight: "8px" }} />
                    <h6 className="text-uppercase font-weight-bold mb-0" style={{ color: selectedTheme.primary, letterSpacing: "1px" }}>
                      Education
                    </h6>
                  </div>
                  {resumeData.educations.map((edu, idx) => (
                    <div key={idx} className="mb-2 small">
                      <div className="font-weight-bold text-dark">{edu.degree}</div>
                      <div className="text-muted">{edu.institution} • {edu.year}</div>
                    </div>
                  ))}
                </div>
              )}
            </Col>

            <Col sm="6">
              {resumeData.skills && resumeData.skills.length > 0 && (
                <div className="resume-section mb-4">
                  <div className="d-flex align-items-center mb-2">
                    <div style={{ width: "4px", height: "18px", backgroundColor: selectedTheme.primary, marginRight: "8px" }} />
                    <h6 className="text-uppercase font-weight-bold mb-0" style={{ color: selectedTheme.primary, letterSpacing: "1px" }}>
                      Expertise
                    </h6>
                  </div>
                  <div className="d-flex flex-wrap" style={{ gap: "4px" }}>
                    {resumeData.skills.map((skill, idx) => (
                      <span key={idx} className="badge badge-secondary py-1 px-2 font-weight-normal" style={{ fontSize: "11px" }}>
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Col>
          </Row>
        </div>
      </div>
    );
  };

  // 3. CLEAN ATS MINIMAL TEMPLATE (100% Parsable)
  const renderMinimalAtsTemplate = () => {
    return (
      <div className="resume-content-body" style={{ color: "#111827", fontFamily: "'Inter', Arial, sans-serif" }}>
        {/* ATS Clean Center Header */}
        <div className="text-center pb-3 mb-3 border-bottom" style={{ borderColor: "#111827" }}>
          <h1 className="h3 font-weight-bold text-uppercase mb-1 text-dark" style={{ letterSpacing: "1px" }}>
            {resumeData.fullName || "CANDIDATE NAME"}
          </h1>
          <div className="small font-weight-bold text-uppercase text-secondary mb-2">
            {resumeData.jobTitle || "TARGET ROLE"}
          </div>
          <div className="small text-dark d-flex justify-content-center flex-wrap" style={{ gap: "10px" }}>
            {resumeData.location && <span>{resumeData.location}</span>}
            {resumeData.phone && <span>• {resumeData.phone}</span>}
            {resumeData.email && <span>• {resumeData.email}</span>}
            {resumeData.linkedin && <span>• {resumeData.linkedin}</span>}
          </div>
        </div>

        {/* Summary */}
        {resumeData.summary && (
          <div className="resume-section mb-3">
            <div className="font-weight-bold text-uppercase small text-dark border-bottom pb-1 mb-2" style={{ letterSpacing: "0.5px" }}>
              SUMMARY
            </div>
            <p className="small mb-0 text-dark" style={{ lineHeight: "1.6", whiteSpace: "pre-line" }}>
              {resumeData.summary}
            </p>
          </div>
        )}

        {/* Experience */}
        {resumeData.experiences && resumeData.experiences.length > 0 && (
          <div className="resume-section mb-3">
            <div className="font-weight-bold text-uppercase small text-dark border-bottom pb-1 mb-2" style={{ letterSpacing: "0.5px" }}>
              EXPERIENCE
            </div>
            {resumeData.experiences.map((exp, idx) => (
              <div key={idx} className="mb-3">
                <div className="d-flex justify-content-between font-weight-bold small text-dark">
                  <span>{exp.title}, {exp.company}</span>
                  <span>{exp.startDate} – {exp.isCurrent ? "Present" : exp.endDate}</span>
                </div>
                {exp.location && <div className="small text-muted">{exp.location}</div>}
                {exp.description && (
                  <div className="small text-dark mt-1 pl-2" style={{ lineHeight: "1.5", whiteSpace: "pre-line" }}>
                    {exp.description}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Education */}
        {resumeData.educations && resumeData.educations.length > 0 && (
          <div className="resume-section mb-3">
            <div className="font-weight-bold text-uppercase small text-dark border-bottom pb-1 mb-2" style={{ letterSpacing: "0.5px" }}>
              EDUCATION
            </div>
            {resumeData.educations.map((edu, idx) => (
              <div key={idx} className="d-flex justify-content-between small text-dark mb-1">
                <div>
                  <strong>{edu.degree}</strong>, {edu.institution} {edu.grade ? `(${edu.grade})` : ""}
                </div>
                <div className="font-weight-bold">{edu.year}</div>
              </div>
            ))}
          </div>
        )}

        {/* Skills */}
        {resumeData.skills && resumeData.skills.length > 0 && (
          <div className="resume-section mb-2">
            <div className="font-weight-bold text-uppercase small text-dark border-bottom pb-1 mb-2" style={{ letterSpacing: "0.5px" }}>
              TECHNICAL & CORE SKILLS
            </div>
            <div className="small text-dark" style={{ lineHeight: "1.6" }}>
              {resumeData.skills.join(" • ")}
            </div>
          </div>
        )}
      </div>
    );
  };

  // 4. SIDEBAR TWO-COLUMN PROFESSIONAL TEMPLATE
  const renderSplitTemplate = () => {
    return (
      <div className="d-flex flex-wrap" style={{ minHeight: "650px" }}>
        {/* Left Sidebar (35%) */}
        <div
          style={{
            flex: "0 0 35%",
            maxWidth: "35%",
            backgroundColor: selectedTheme.light,
            borderRight: `1px solid ${selectedTheme.secondary}20`,
            padding: "30px 20px"
          }}
        >
          {resumeData.photoUrl && (
            <div className="text-center mb-3">
              <img
                src={resumeData.photoUrl}
                alt="Profile"
                style={{
                  width: "110px",
                  height: "110px",
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: `3px solid ${selectedTheme.primary}`
                }}
              />
            </div>
          )}

          {/* Contact Details */}
          <div className="mb-4">
            <h6 className="font-weight-bold text-uppercase pb-1 mb-2" style={{ color: selectedTheme.primary, borderBottom: `2px solid ${selectedTheme.primary}`, fontSize: "12px", letterSpacing: "0.5px" }}>
              Contact
            </h6>
            <div className="small text-secondary" style={{ fontSize: "12px", lineHeight: "1.8" }}>
              {resumeData.email && <div className="text-break">✉ {resumeData.email}</div>}
              {resumeData.phone && <div>📞 {resumeData.phone}</div>}
              {resumeData.location && <div>📍 {resumeData.location}</div>}
              {resumeData.linkedin && <div className="text-break">🔗 {resumeData.linkedin}</div>}
            </div>
          </div>

          {/* Education in Sidebar */}
          {resumeData.educations && resumeData.educations.length > 0 && (
            <div className="mb-4">
              <h6 className="font-weight-bold text-uppercase pb-1 mb-2" style={{ color: selectedTheme.primary, borderBottom: `2px solid ${selectedTheme.primary}`, fontSize: "12px", letterSpacing: "0.5px" }}>
                Education
              </h6>
              {resumeData.educations.map((edu, idx) => (
                <div key={idx} className="mb-2 small" style={{ fontSize: "12px" }}>
                  <strong className="text-dark d-block">{edu.degree}</strong>
                  <span className="text-muted">{edu.institution}</span>
                  <div className="text-secondary">{edu.year}</div>
                </div>
              ))}
            </div>
          )}

          {/* Skills in Sidebar */}
          {resumeData.skills && resumeData.skills.length > 0 && (
            <div className="mb-3">
              <h6 className="font-weight-bold text-uppercase pb-1 mb-2" style={{ color: selectedTheme.primary, borderBottom: `2px solid ${selectedTheme.primary}`, fontSize: "12px", letterSpacing: "0.5px" }}>
                Key Skills
              </h6>
              <div className="d-flex flex-wrap" style={{ gap: "4px" }}>
                {resumeData.skills.map((skill, idx) => (
                  <span
                    key={idx}
                    style={{
                      backgroundColor: "#ffffff",
                      color: selectedTheme.primary,
                      border: `1px solid ${selectedTheme.secondary}40`,
                      borderRadius: "4px",
                      padding: "2px 8px",
                      fontSize: "11px",
                      fontWeight: "600"
                    }}
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Main Column (65%) */}
        <div style={{ flex: "0 0 65%", maxWidth: "65%", padding: "30px 30px" }}>
          <div className="mb-4">
            <h1 className="h3 font-weight-bold mb-1" style={{ color: selectedTheme.primary }}>
              {resumeData.fullName || "YOUR FULL NAME"}
            </h1>
            <div className="h6 font-weight-bold text-uppercase" style={{ color: selectedTheme.secondary, letterSpacing: "1px" }}>
              {resumeData.jobTitle || "PROFESSIONAL ROLE"}
            </div>
          </div>

          {/* Summary */}
          {resumeData.summary && (
            <div className="mb-4">
              <h6 className="text-uppercase font-weight-bold pb-1 mb-2" style={{ color: selectedTheme.primary, borderBottom: `1px solid #e2e8f0`, fontSize: "13px" }}>
                About Me
              </h6>
              <p className="small text-secondary mb-0" style={{ lineHeight: "1.7", whiteSpace: "pre-line" }}>
                {resumeData.summary}
              </p>
            </div>
          )}

          {/* Experience */}
          {resumeData.experiences && resumeData.experiences.length > 0 && (
            <div className="mb-4">
              <h6 className="text-uppercase font-weight-bold pb-1 mb-3" style={{ color: selectedTheme.primary, borderBottom: `1px solid #e2e8f0`, fontSize: "13px" }}>
                Professional Experience
              </h6>
              {resumeData.experiences.map((exp, idx) => (
                <div key={idx} className="mb-3">
                  <div className="d-flex justify-content-between align-items-baseline">
                    <span className="font-weight-bold text-dark" style={{ fontSize: "13px" }}>
                      {exp.title}
                    </span>
                    <span className="small text-muted font-weight-bold">{exp.startDate} – {exp.isCurrent ? "Present" : exp.endDate}</span>
                  </div>
                  <div className="small font-weight-bold mb-1" style={{ color: selectedTheme.secondary }}>
                    {exp.company}
                  </div>
                  {exp.description && (
                    <div className="small text-secondary" style={{ lineHeight: "1.6", whiteSpace: "pre-line" }}>
                      {exp.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render current template in container
  const renderCurrentTemplate = () => {
    switch (selectedTemplate) {
      case "executive":
        return renderExecutiveTemplate();
      case "minimal":
        return renderMinimalAtsTemplate();
      case "split":
        return renderSplitTemplate();
      case "modern":
      default:
        return renderModernTemplate();
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" centered className="resume-builder-modal">
      {/* MODAL HEADER */}
      <ModalHeader toggle={toggle} className="bg-white border-bottom py-3">
        <div className="d-flex align-items-center justify-content-between w-100 pr-3">
          <div className="d-flex align-items-center">
            <div
              className="rounded-circle p-2 mr-3 d-flex align-items-center justify-content-center"
              style={{ backgroundColor: selectedTheme.light, color: selectedTheme.primary }}
            >
              <FileText size={22} />
            </div>
            <div>
              <div className="d-flex align-items-center">
                <h5 className="mb-0 font-weight-bold text-dark mr-2">Professional ATS Resume Studio</h5>
                <Badge pill style={{ backgroundColor: "#c16d18", borderColor: "#c16d18", color: "white", fontSize: "10px", padding: "4px 8px" }}>
                  ATS-Optimized 2026
                </Badge>
              </div>
              <small className="text-muted">
                Craft industry-standard, high-conversion resumes with multiple modern templates
              </small>
            </div>
          </div>
        </div>
      </ModalHeader>

      {/* MODAL BODY */}
      <ModalBody className="p-0 bg-light">
        {/* Navigation Tabs Bar */}
        <div className="bg-white border-bottom px-4 pt-2 d-flex justify-content-between align-items-center flex-wrap">
          <Nav tabs className="border-bottom-0">
            <NavItem>
              <NavLink
                className={activeTab === "1" ? "active border-primary text-primary" : "text-secondary"}
                onClick={() => setActiveTab("1")}
                style={{ cursor: "pointer", fontWeight: "600", borderBottom: activeTab === "1" ? "3px solid" : "none" }}
              >
                <User size={15} className="mr-1" /> 1. Edit Content
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === "2" ? "active border-primary text-primary" : "text-secondary"}
                onClick={() => setActiveTab("2")}
                style={{ cursor: "pointer", fontWeight: "600", borderBottom: activeTab === "2" ? "3px solid" : "none" }}
              >
                <Sliders size={15} className="mr-1" /> 2. Templates & Styling
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === "3" ? "active border-primary text-primary" : "text-secondary"}
                onClick={() => setActiveTab("3")}
                style={{ cursor: "pointer", fontWeight: "600", borderBottom: activeTab === "3" ? "3px solid" : "none" }}
              >
                <Eye size={15} className="mr-1" /> 3. Live Preview & Export
              </NavLink>
            </NavItem>
          </Nav>

          <div className="d-flex align-items-center py-2" style={{ gap: "8px" }}>
            <button
              type="button"
              className="btn btn-sm d-inline-flex align-items-center font-weight-bold"
              onClick={() => handleAutoFillFromProfile(true)}
              title="Reload data from candidate profile"
              style={{
                backgroundColor: "#f8fafc",
                color: "#1e293b",
                border: "1px solid #cbd5e1",
                borderRadius: "6px",
                padding: "6px 12px",
                fontSize: "0.85rem",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#e2e8f0";
                e.currentTarget.style.color = "#0f172a";
                e.currentTarget.style.borderColor = "#94a3b8";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#f8fafc";
                e.currentTarget.style.color = "#1e293b";
                e.currentTarget.style.borderColor = "#cbd5e1";
              }}
            >
              <RefreshCw size={14} className="mr-1" style={{ color: "#2563eb" }} /> Sync from Profile
            </button>
            <Button
              type="button"
              size="sm"
              onClick={() => setActiveTab("3")}
              style={{
                backgroundColor: "#c16d18",
                borderColor: "#c16d18",
                color: "white",
                borderRadius: "6px",
                padding: "6px 14px",
                fontSize: "0.85rem",
                fontWeight: "600",
                display: "inline-flex",
                alignItems: "center"
              }}
            >
              <Eye size={14} className="mr-1" /> View Preview
            </Button>
          </div>
        </div>

        {/* TAB CONTENTS */}
        <TabContent activeTab={activeTab} className="p-4" style={{ minHeight: "550px", maxHeight: "75vh", overflowY: "auto" }}>

          {/* ================================================== */}
          {/* TAB 1: CONTENT EDITOR */}
          {/* ================================================== */}
          <TabPane tabId="1">
            {/* SECTION: PERSONAL INFORMATION */}
            <Card className="border shadow-sm mb-4">
              <CardBody>
                <h6 className="font-weight-bold mb-3 d-flex align-items-center" style={{ color: selectedTheme.primary }}>
                  <User size={18} className="mr-2" /> Personal & Contact Information
                </h6>
                <Row>
                  <Col md="4" sm="6">
                    <FormGroup>
                      <Label className="small font-weight-bold">Full Name *</Label>
                      <Input
                        type="text"
                        value={resumeData.fullName}
                        onChange={(e) => handleFieldChange("fullName", e.target.value)}
                        placeholder="e.g. John Doe"
                      />
                    </FormGroup>
                  </Col>

                  <Col md="4" sm="6">
                    <FormGroup>
                      <Label className="small font-weight-bold">Target Job Title / Role *</Label>
                      <Input
                        type="text"
                        value={resumeData.jobTitle}
                        onChange={(e) => handleFieldChange("jobTitle", e.target.value)}
                        placeholder="e.g. Senior Auditor / Financial Analyst"
                      />
                    </FormGroup>
                  </Col>

                  <Col md="4" sm="6">
                    <FormGroup>
                      <Label className="small font-weight-bold">Email Address *</Label>
                      <Input
                        type="email"
                        value={resumeData.email}
                        onChange={(e) => handleFieldChange("email", e.target.value)}
                        placeholder="e.g. name@example.com"
                      />
                    </FormGroup>
                  </Col>

                  <Col md="4" sm="6">
                    <FormGroup>
                      <Label className="small font-weight-bold">Phone Number *</Label>
                      <Input
                        type="text"
                        value={resumeData.phone}
                        onChange={(e) => handleFieldChange("phone", e.target.value)}
                        placeholder="e.g. +91 9876543210"
                      />
                    </FormGroup>
                  </Col>

                  <Col md="4" sm="6">
                    <FormGroup>
                      <Label className="small font-weight-bold">Location (City, State, Country)</Label>
                      <Input
                        type="text"
                        value={resumeData.location}
                        onChange={(e) => handleFieldChange("location", e.target.value)}
                        placeholder="e.g. Mumbai, Maharashtra, India"
                      />
                    </FormGroup>
                  </Col>

                  <Col md="4" sm="6">
                    <FormGroup>
                      <Label className="small font-weight-bold">LinkedIn / Portfolio URL</Label>
                      <Input
                        type="text"
                        value={resumeData.linkedin}
                        onChange={(e) => handleFieldChange("linkedin", e.target.value)}
                        placeholder="e.g. linkedin.com/in/username"
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* SECTION: PROFESSIONAL SUMMARY */}
            <Card className="border shadow-sm mb-4">
              <CardBody>
                <h6 className="font-weight-bold mb-3 d-flex align-items-center" style={{ color: selectedTheme.primary }}>
                  <FileText size={18} className="mr-2" /> Professional Executive Summary
                </h6>
                <FormGroup>
                  <Label className="small text-muted">
                    Provide a compelling 2-4 sentence summary of your background, core strengths, and quantifiable achievements.
                  </Label>
                  <Input
                    type="textarea"
                    rows="4"
                    value={resumeData.summary}
                    onChange={(e) => handleFieldChange("summary", e.target.value)}
                    placeholder="Results-driven professional with X years of experience in..."
                  />
                </FormGroup>
              </CardBody>
            </Card>

            {/* SECTION: WORK EXPERIENCE (DYNAMIC LIST) */}
            <Card className="border shadow-sm mb-4">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="font-weight-bold mb-0 d-flex align-items-center" style={{ color: selectedTheme.primary }}>
                    <Briefcase size={18} className="mr-2" /> Work Experience History
                  </h6>
                  <Button style={{ backgroundColor: "#c16d18", borderColor: "#c16d18", color: "white" }} size="sm" onClick={handleAddExperience}>
                    <Plus size={14} className="mr-1" /> Add Experience
                  </Button>
                </div>

                {resumeData.experiences.length === 0 ? (
                  <div className="text-center py-4 text-muted bg-white rounded border border-dashed">
                    <Briefcase size={28} className="text-muted mb-2" />
                    <div>No work experiences added yet. Click &quot;Add Experience&quot; to begin.</div>
                  </div>
                ) : (
                  resumeData.experiences.map((exp, idx) => (
                    <div key={exp.id || idx} className="p-3 mb-3 bg-white rounded border">
                      <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                        <span className="font-weight-bold text-primary small">
                          Experience #{resumeData.experiences.length - idx}
                        </span>
                        <Button
                          color="link"
                          className="text-danger p-0"
                          size="sm"
                          onClick={() => handleDeleteExperience(exp.id)}
                          title="Delete this item"
                        >
                          <Trash2 size={15} />
                        </Button>
                      </div>

                      <Row>
                        <Col md="6">
                          <FormGroup>
                            <Label className="small font-weight-bold">Job Title</Label>
                            <Input
                              type="text"
                              value={exp.title}
                              onChange={(e) => handleUpdateExperience(exp.id, "title", e.target.value)}
                              placeholder="e.g. Senior Auditor"
                            />
                          </FormGroup>
                        </Col>
                        <Col md="6">
                          <FormGroup>
                            <Label className="small font-weight-bold">Company / Employer</Label>
                            <Input
                              type="text"
                              value={exp.company}
                              onChange={(e) => handleUpdateExperience(exp.id, "company", e.target.value)}
                              placeholder="e.g. ABC Auditing Services"
                            />
                          </FormGroup>
                        </Col>
                        <Col md="4">
                          <FormGroup>
                            <Label className="small font-weight-bold">Location</Label>
                            <Input
                              type="text"
                              value={exp.location}
                              onChange={(e) => handleUpdateExperience(exp.id, "location", e.target.value)}
                              placeholder="e.g. Mumbai, India"
                            />
                          </FormGroup>
                        </Col>
                        <Col md="4">
                          <FormGroup>
                            <Label className="small font-weight-bold">Start Date</Label>
                            <Input
                              type="text"
                              value={exp.startDate}
                              onChange={(e) => handleUpdateExperience(exp.id, "startDate", e.target.value)}
                              placeholder="e.g. Jan 2022"
                            />
                          </FormGroup>
                        </Col>
                        <Col md="4">
                          <FormGroup>
                            <Label className="small font-weight-bold">End Date (or Present)</Label>
                            <Input
                              type="text"
                              value={exp.endDate}
                              onChange={(e) => handleUpdateExperience(exp.id, "endDate", e.target.value)}
                              placeholder="e.g. Present"
                            />
                          </FormGroup>
                        </Col>
                        <Col md="12">
                          <FormGroup className="mb-0">
                            <Label className="small font-weight-bold">Responsibilities & Key Achievements</Label>
                            <Input
                              type="textarea"
                              rows="3"
                              value={exp.description}
                              onChange={(e) => handleUpdateExperience(exp.id, "description", e.target.value)}
                              placeholder="• Highlight your core achievements and measurable impact&#10;• Led audit reviews for 50+ business units&#10;• Reduced compliance errors by 25%"
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    </div>
                  ))
                )}
              </CardBody>
            </Card>

            {/* SECTION: EDUCATION (DYNAMIC LIST) */}
            <Card className="border shadow-sm mb-4">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="font-weight-bold mb-0 d-flex align-items-center" style={{ color: selectedTheme.primary }}>
                    <BookOpen size={18} className="mr-2" /> Education & Qualifications
                  </h6>
                  <Button style={{ backgroundColor: "#c16d18", borderColor: "#c16d18", color: "white" }} size="sm" onClick={handleAddEducation}>
                    <Plus size={14} className="mr-1" /> Add Education
                  </Button>
                </div>

                {resumeData.educations.map((edu, idx) => (
                  <div key={edu.id || idx} className="p-3 mb-3 bg-white rounded border">
                    <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                      <span className="font-weight-bold text-primary small">
                        Degree #{resumeData.educations.length - idx}
                      </span>
                      <Button
                        color="link"
                        className="text-danger p-0"
                        size="sm"
                        onClick={() => handleDeleteEducation(edu.id)}
                      >
                        <Trash2 size={15} />
                      </Button>
                    </div>

                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <Label className="small font-weight-bold">Degree / Course Name</Label>
                          <Input
                            type="text"
                            value={edu.degree}
                            onChange={(e) => handleUpdateEducation(edu.id, "degree", e.target.value)}
                            placeholder="e.g. B.Com / MBA / CA Inter"
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label className="small font-weight-bold">Institution / University</Label>
                          <Input
                            type="text"
                            value={edu.institution}
                            onChange={(e) => handleUpdateEducation(edu.id, "institution", e.target.value)}
                            placeholder="e.g. Delhi University"
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup className="mb-0">
                          <Label className="small font-weight-bold">Year of Completion</Label>
                          <Input
                            type="text"
                            value={edu.year}
                            onChange={(e) => handleUpdateEducation(edu.id, "year", e.target.value)}
                            placeholder="e.g. 2021"
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup className="mb-0">
                          <Label className="small font-weight-bold">Grade / Percentage / Honors</Label>
                          <Input
                            type="text"
                            value={edu.grade}
                            onChange={(e) => handleUpdateEducation(edu.id, "grade", e.target.value)}
                            placeholder="e.g. First Class Distinction / 8.5 CGPA"
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                ))}
              </CardBody>
            </Card>

            {/* SECTION: SKILLS */}
            <Card className="border shadow-sm mb-4">
              <CardBody>
                <h6 className="font-weight-bold mb-3 d-flex align-items-center" style={{ color: selectedTheme.primary }}>
                  <Award size={18} className="mr-2" /> Key Skills & Competencies
                </h6>
                <div className="d-flex mb-3">
                  <Input
                    type="text"
                    value={newSkillInput}
                    onChange={(e) => setNewSkillInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddSkill();
                      }
                    }}
                    placeholder="Type a skill and press Enter or click Add (e.g. Tax Audit, Risk Management, Python)"
                    className="mr-2"
                  />
                  <Button style={{ backgroundColor: "#c16d18", borderColor: "#c16d18", color: "white", whiteSpace: "nowrap" }} className="px-4" onClick={handleAddSkill}>
                    Add Skill
                  </Button>
                </div>

                <div className="d-flex flex-wrap" style={{ gap: "8px" }}>
                  {resumeData.skills.map((skill, idx) => (
                    <Badge
                      key={idx}
                      color="light"
                      className="border text-dark d-flex align-items-center py-2 px-3"
                      style={{ fontSize: "13px", borderRadius: "20px" }}
                    >
                      <span className="mr-2">{skill}</span>
                      <span
                        style={{ cursor: "pointer", color: "#ef4444", fontWeight: "bold" }}
                        onClick={() => handleDeleteSkill(skill)}
                      >
                        ×
                      </span>
                    </Badge>
                  ))}
                </div>
              </CardBody>
            </Card>

            {/* SECTION: CERTIFICATIONS */}
            <Card className="border shadow-sm mb-2">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="font-weight-bold mb-0 d-flex align-items-center" style={{ color: selectedTheme.primary }}>
                    <Award size={18} className="mr-2" /> Certifications & Licenses
                  </h6>
                  <Button style={{ backgroundColor: "#c16d18", borderColor: "#c16d18", color: "white" }} size="sm" onClick={handleAddCertification}>
                    <Plus size={14} className="mr-1" /> Add Certification
                  </Button>
                </div>

                {resumeData.certifications.map((cert, idx) => (
                  <div key={cert.id || idx} className="d-flex align-items-center mb-2" style={{ gap: "10px" }}>
                    <Input
                      type="text"
                      placeholder="Certificate Name"
                      value={cert.name}
                      onChange={(e) => handleUpdateCertification(cert.id, "name", e.target.value)}
                      style={{ flex: "2" }}
                    />
                    <Input
                      type="text"
                      placeholder="Issuing Body"
                      value={cert.issuer}
                      onChange={(e) => handleUpdateCertification(cert.id, "issuer", e.target.value)}
                      style={{ flex: "2" }}
                    />
                    <Input
                      type="text"
                      placeholder="Year"
                      value={cert.year}
                      onChange={(e) => handleUpdateCertification(cert.id, "year", e.target.value)}
                      style={{ flex: "1" }}
                    />
                    <Button
                      color="link"
                      className="text-danger p-0"
                      onClick={() => handleDeleteCertification(cert.id)}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))}
              </CardBody>
            </Card>
          </TabPane>

          {/* ================================================== */}
          {/* TAB 2: TEMPLATES & STYLING */}
          {/* ================================================== */}
          <TabPane tabId="2">
            {/* Template Chooser */}
            <h6 className="font-weight-bold mb-3 text-dark">Select Resume Template</h6>
            <Row className="mb-4">
              {TEMPLATES.map((tmpl) => {
                const isSelected = selectedTemplate === tmpl.id;
                return (
                  <Col md="6" lg="3" key={tmpl.id} className="mb-3">
                    <div
                      onClick={() => setSelectedTemplate(tmpl.id)}
                      className="p-3 bg-white rounded border position-relative h-100 shadow-sm"
                      style={{
                        cursor: "pointer",
                        borderColor: isSelected ? selectedTheme.primary : "#e2e8f0",
                        borderWidth: isSelected ? "2px" : "1px",
                        backgroundColor: isSelected ? selectedTheme.light : "#ffffff",
                        transition: "all 0.2s ease"
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <span className="font-weight-bold text-dark">{tmpl.name}</span>
                        {isSelected && (
                          <div
                            className="rounded-circle text-white d-flex align-items-center justify-content-center"
                            style={{ width: "20px", height: "20px", backgroundColor: selectedTheme.primary }}
                          >
                            <Check size={12} />
                          </div>
                        )}
                      </div>
                      <Badge color="secondary" className="mb-2">
                        {tmpl.badge}
                      </Badge>
                      <p className="small text-muted mb-0" style={{ fontSize: "12px" }}>
                        {tmpl.description}
                      </p>
                    </div>
                  </Col>
                );
              })}
            </Row>

            {/* Color Accent Picker */}
            <h6 className="font-weight-bold mb-3 text-dark">Select Accent Color Scheme</h6>
            <Row className="mb-4">
              {COLOR_THEMES.map((theme) => {
                const isSelected = selectedTheme.id === theme.id;
                return (
                  <Col md="4" sm="6" key={theme.id} className="mb-3">
                    <div
                      onClick={() => setSelectedTheme(theme)}
                      className="p-3 bg-white rounded border d-flex align-items-center shadow-sm"
                      style={{
                        cursor: "pointer",
                        borderColor: isSelected ? theme.primary : "#e2e8f0",
                        borderWidth: isSelected ? "2px" : "1px",
                        transition: "all 0.2s ease"
                      }}
                    >
                      <div
                        className="rounded-circle mr-3"
                        style={{
                          width: "36px",
                          height: "36px",
                          backgroundColor: theme.primary,
                          border: "2px solid #ffffff",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                        }}
                      />
                      <div className="flex-grow-1">
                        <div className="font-weight-bold text-dark small">{theme.name}</div>
                        <div className="text-muted" style={{ fontSize: "11px" }}>{theme.primary}</div>
                      </div>
                      {isSelected && <Check size={16} style={{ color: theme.primary }} />}
                    </div>
                  </Col>
                );
              })}
            </Row>

            {/* Font Style Picker */}
            <h6 className="font-weight-bold mb-3 text-dark">Typography Style</h6>
            <Row>
              {FONT_PRESETS.map((font) => {
                const isSelected = selectedFont.id === font.id;
                return (
                  <Col md="4" key={font.id} className="mb-3">
                    <div
                      onClick={() => setSelectedFont(font)}
                      className="p-3 bg-white rounded border shadow-sm"
                      style={{
                        cursor: "pointer",
                        borderColor: isSelected ? selectedTheme.primary : "#e2e8f0",
                        borderWidth: isSelected ? "2px" : "1px",
                        fontFamily: font.fontFamily
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="font-weight-bold text-dark">{font.name}</span>
                        {isSelected && <Check size={16} style={{ color: selectedTheme.primary }} />}
                      </div>
                      <div className="text-muted small">Aa Bb Cc 123 • Professional Resume Text</div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </TabPane>

          {/* TAB 3: LIVE PREVIEW & EXPORT */}
          <TabPane tabId="3">
            {/* Quick action bar */}
            <div className="d-flex justify-content-between align-items-center mb-3 bg-white p-3 rounded border shadow-sm flex-wrap" style={{ gap: "10px" }}>
              <div className="d-flex align-items-center">
                <Badge style={{ backgroundColor: "#c16d18", borderColor: "#c16d18", color: "white" }} className="mr-2 py-1 px-2">Live Render</Badge>
                <span className="small text-muted">
                  Template: <strong>{TEMPLATES.find((t) => t.id === selectedTemplate)?.name}</strong> | Color: <strong>{selectedTheme.name}</strong>
                </span>
              </div>

              <div className="d-flex" style={{ gap: "8px" }}>
                <Button
                  color="secondary"
                  outline
                  size="sm"
                  onClick={handleDownloadHtml}
                  style={{ color: "#475569", borderColor: "#cbd5e1", fontWeight: "500" }}
                >
                  <Download size={14} className="mr-1" /> Download HTML
                </Button>
                <Button style={{ backgroundColor: "#c16d18", borderColor: "#c16d18", color: "white" }} size="sm" onClick={handlePrint}>
                  <Printer size={14} className="mr-1" /> Print / Save as PDF
                </Button>
              </div>
            </div>

            {/* Resume Container Target for Printing and Preview */}
            <div
              className="d-flex justify-content-center p-2"
              style={{ backgroundColor: "#e2e8f0", borderRadius: "8px" }}
            >
              <style>{`
                .resume-content-body {
                  padding: 35px 40px;
                }
              `}</style>
              <div
                id="printable-resume-target"
                className="bg-white shadow-lg w-100"
                style={{
                  maxWidth: "850px",
                  borderRadius: "6px",
                  overflow: "hidden",
                  fontFamily: selectedFont.fontFamily
                }}
              >
                {renderCurrentTemplate()}
              </div>
            </div>
          </TabPane>
        </TabContent>
      </ModalBody>

      {/* MODAL FOOTER */}
      <ModalFooter className="bg-white border-top d-flex justify-content-between">
        <Button
          type="button"
          color="secondary"
          outline
          onClick={toggle}
          style={{ color: "#475569", borderColor: "#cbd5e1", fontWeight: "500" }}
        >
          Cancel
        </Button>

        <div className="d-flex" style={{ gap: "8px" }}>
          <Button
            type="button"
            color="secondary"
            outline
            onClick={handlePrint}
            style={{ color: "#475569", borderColor: "#cbd5e1", fontWeight: "500" }}
          >
            <Printer size={15} className="mr-1" /> Print / PDF
          </Button>
          <Button type="button" style={{ backgroundColor: "#c16d18", borderColor: "#c16d18", color: "white" }} onClick={handleAttachToProfile}>
            <CheckCircle size={15} className="mr-1" /> Use & Attach to Profile
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ResumeBuilderModal;

